import React from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as IconBatteryCharging } from '../../../assets/images/icons/icon_battery_charging.svg';
import { ReactComponent as IconCellular } from '../../../assets/images/icons/icon_cellular.svg';
import { ReactComponent as IconCheckCircle } from '../../../assets/images/icons/check_circle_filled.svg';
import { ReactComponent as IconDrip } from '../../../assets/images/icons/icon_drip.svg';
import { ReactComponent as IconFeather } from '../../../assets/images/icons/icon_feather.svg';
import { ReactComponent as IconMagic } from '../../../assets/images/icons/icon_magic.svg';
import { ReactComponent as IconStar } from '../../../assets/images/icons/star.svg';
import Question from '../../../views/ProductDetails/Series3CollarDetails/components/FAQ/Question';
import { WhatsInTheBox } from '../HardwareFirstBelowFold';
import pageStyles from '../HardwareFirstPickYourFi.module.scss';
import sectionStyles from '../HardwareFirstSections.module.scss';
import soloStyles from '../HardwareFirstSolo.module.scss';
import styles from './CatFirstSections.module.scss';

type SvgIcon = React.FC<React.SVGProps<SVGSVGElement>>;

// the front-on render the box section uses: the tracker alone, no hand or case
const MINI_DEVICE_IMAGE = '/product_images/mini/box/device.png';
// the cats page's existing app screenshot; the folder only holds its sized variants
const CAT_APP_SCREEN = '/product_images/smart-collar-v3b/app-ui-homescreen-1-cat@1000w';

/** A collar band with the tracker clipped to its front: the "fits any collar" tile. */
function IconCollar(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...props}
    >
      <path d="M8.5 15.7C5.3 15.1 3 13.7 3 12c0-2.2 4-4 9-4s9 1.8 9 4c0 1.7-2.3 3.1-5.5 3.7" />
      <rect x="8.5" y="13" width="7" height="7" rx="1.5" />
    </svg>
  );
}

const HERO_TILES: { Icon: SvgIcon; key: string }[] = [
  { Icon: IconFeather, key: 'cat_first_tile_weight' },
  { Icon: IconDrip, key: 'cat_first_tile_waterproof' },
  { Icon: IconBatteryCharging, key: 'cat_first_tile_battery' },
  { Icon: IconCollar, key: 'cat_first_tile_collar' },
];

/**
 * The cat-first hero's four tiles, in the solo page's tile style. Cat owners' first doubt is bulk,
 * so the hero answers it with the hardware (weight, waterproofing, battery, fit) where the dog
 * pages list what Fi does; the tracking pitch moves to the outside-time band below.
 */
export function CatFirstFeatures() {
  const { t } = useTranslation();
  return (
    <div className={soloStyles.features}>
      <ul className={soloStyles.tiles}>
        {HERO_TILES.map(({ Icon, key }) => (
          <li key={key} className={soloStyles.tile}>
            <span className={soloStyles.tileIcon}>
              <Icon />
            </span>
            <span>{t(key)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

const ROAM_ROWS = [
  { titleKey: 'cat_first_roam_alerts_title', subKey: 'cat_first_roam_alerts_sub' },
  { titleKey: 'cat_first_roam_lost_title', subKey: 'cat_first_roam_lost_sub' },
  { titleKey: 'cat_first_roam_spots_title', subKey: 'cat_first_roam_spots_sub' },
];

// hot spots on the map: home, the yard two gardens over, the path between and two quieter corners.
// each point is drawn in black at low opacity; the filter blurs the overlap into density and maps
// it onto the blue-to-red ramp, so overlapping points read hotter, as a real heatmap does. the
// opacity sits on each circle, not the group: a group's opacity flattens it to one layer first, so
// its overlaps would never add up past the group's own opacity.
const HEAT_POINTS: { opacity: number; points: [number, number, number][] }[] = [
  {
    opacity: 0.52,
    points: [
      [600, 355, 24],
      [590, 344, 22],
      [613, 362, 22],
      [606, 340, 20],
      [584, 366, 20],
      [622, 349, 20],
      [598, 374, 18],
    ],
  },
  {
    opacity: 0.48,
    points: [
      [745, 268, 26],
      [734, 259, 22],
      [758, 276, 22],
      [751, 254, 20],
      [727, 279, 20],
      [766, 262, 20],
      [740, 287, 18],
      [756, 291, 16],
      [744, 268, 14],
    ],
  },
  {
    opacity: 0.4,
    points: [
      [632, 331, 16],
      [656, 312, 16],
      [681, 297, 16],
      [706, 283, 16],
    ],
  },
  {
    opacity: 0.44,
    points: [
      [886, 331, 20],
      [875, 322, 18],
      [896, 341, 16],
      [891, 318, 14],
    ],
  },
  {
    opacity: 0.36,
    points: [
      [811, 210, 16],
      [821, 222, 14],
      [842, 290, 12],
      [815, 304, 12],
    ],
  },
];

/** The illustrated neighbourhood under the outside-time band, with the heatmap over it. */
function RoamMap() {
  return (
    <svg className={styles.roamMap} viewBox="380 120 600 360" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      <defs>
        <filter
          id="cat-first-heat"
          filterUnits="userSpaceOnUse"
          x="380"
          y="120"
          width="600"
          height="360"
          colorInterpolationFilters="sRGB"
        >
          <feGaussianBlur stdDeviation="16" result="blur" />
          <feColorMatrix in="blur" type="matrix" values="0 0 0 1 0  0 0 0 1 0  0 0 0 1 0  0 0 0 1 0" />
          <feComponentTransfer>
            <feFuncR type="table" tableValues="0.20 0.10 0.20 0.98 1 1 0.93" />
            <feFuncG type="table" tableValues="0.45 0.70 0.85 0.86 0.62 0.30 0.10" />
            <feFuncB type="table" tableValues="0.95 0.85 0.35 0.10 0.05 0.05 0.12" />
            <feFuncA type="table" tableValues="0 0.45 0.62 0.72 0.80 0.86 0.92" />
          </feComponentTransfer>
        </filter>
      </defs>
      <rect width="1000" height="480" fill="#F1F1EE" />
      <path d="M1000 480H726c20-72 96-118 206-130l68-6Z" fill="#D8E9D0" />
      <path
        d="M1000 96v104c-66 4-116 24-138 56-24 34-64 46-110 34 26-52 68-94 128-128 30-18 68-40 120-66Z"
        fill="#D9E6F3"
      />
      <g fill="#E3E3DD">
        <rect x="392" y="218" width="60" height="44" rx="7" transform="rotate(-13 422 240)" />
        <rect x="646" y="312" width="104" height="66" rx="8" transform="rotate(-13 698 345)" />
        <rect x="800" y="300" width="86" height="58" rx="8" transform="rotate(-13 843 329)" />
        <rect x="604" y="444" width="92" height="58" rx="8" transform="rotate(-13 650 473)" />
        <rect x="666" y="176" width="62" height="44" rx="7" transform="rotate(-13 697 198)" />
        <rect x="856" y="128" width="66" height="46" rx="7" transform="rotate(-13 889 151)" />
      </g>
      <g fill="none" strokeLinecap="round">
        <path d="M186 -30C250 140 340 300 470 400 600 500 790 530 1040 520" stroke="#E4E4DF" strokeWidth="24" />
        <path d="M778 -30C790 100 806 220 856 320 906 420 956 462 1040 480" stroke="#E4E4DF" strokeWidth="22" />
        <path d="M260 480C330 430 400 374 486 312 560 258 664 224 1040 246" stroke="#E4E4DF" strokeWidth="23" />
        <path d="M186 -30C250 140 340 300 470 400 600 500 790 530 1040 520" stroke="#fff" strokeWidth="17" />
        <path d="M778 -30C790 100 806 220 856 320 906 420 956 462 1040 480" stroke="#fff" strokeWidth="15" />
        <path d="M260 480C330 430 400 374 486 312 560 258 664 224 1040 246" stroke="#fff" strokeWidth="16" />
      </g>
      <g transform="translate(600 360) rotate(-13)">
        <rect x="-36" y="-14" width="54" height="42" rx="4" fill="#D6D6CE" />
        <rect x="18" y="2" width="20" height="26" rx="3" fill="#D6D6CE" />
        <rect x="-58" y="-58" width="116" height="116" fill="#2CB355" opacity=".17" />
        <rect x="-58" y="-58" width="116" height="116" fill="none" stroke="#1FA34A" strokeWidth="3" />
      </g>
      <g filter="url(#cat-first-heat)" fill="#000">
        {HEAT_POINTS.map((group) => (
          <g key={`${group.points[0][0]}-${group.points[0][1]}`}>
            {group.points.map(([cx, cy, r]) => (
              <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r={r} fillOpacity={group.opacity} />
            ))}
          </g>
        ))}
      </g>
    </svg>
  );
}

/** The fi wordmark, for the escape-alert notification's app tile. */
function FiMark() {
  return (
    <svg width="16" height="14" viewBox="0 0 20 17.707" aria-hidden="true">
      <path
        d="M 14.038 0.001 L 12.85 4.003 L 10.5 4.003 C 10.231 4.003 9.84 4.042 9.627 4.12 C 9.114 4.306 8.767 4.713 8.588 5.316 L 8.419 5.886 L 11.542 5.886 L 10.353 9.889 L 7.231 9.889 L 4.911 17.707 L 0 17.707 L 2.32 9.889 L 0.521 9.889 L 1.71 5.886 L 3.508 5.886 L 3.909 4.536 C 4.489 2.583 5.725 1.327 7.083 0.643 C 7.956 0.203 8.762 0 10.4 0 L 14.038 0.001 Z"
        fill="#000"
      />
      <path d="M 14.745 17.707 L 9.834 17.707 L 13.607 4.994 L 18.518 4.994 L 14.745 17.707 Z" fill="#000" />
      <path d="M 18.812 4.003 L 13.902 4.003 L 15.089 0.001 L 20 0.001 L 18.812 4.003 Z" fill="#000" />
    </svg>
  );
}

/**
 * Outside time, the cats-only app feature: an escape alert over a heatmap of the cat's favorite
 * spots, in place of the dog pages' live-gps band.
 */
export function CatFirstRoamBand() {
  const { t } = useTranslation();
  return (
    <section className={styles.roam}>
      <div className={styles.roamCopy}>
        <div className={styles.eyebrow}>{t('cat_first_roam_eyebrow')}</div>
        <h2 className={styles.roamTitle}>{t('cat_first_roam_title')}</h2>
        <p className={styles.roamSub}>{t('cat_first_roam_sub')}</p>
        <ul className={styles.roamList}>
          {ROAM_ROWS.map((row) => (
            <li key={row.titleKey} className={styles.roamItem}>
              <span className={styles.roamCheck}>
                <IconCheckCircle />
              </span>
              <span>
                <span className={styles.roamItemTitle}>{t(row.titleKey)}</span>
                <span className={styles.roamItemSub}>{t(row.subKey)}</span>
              </span>
            </li>
          ))}
        </ul>
      </div>
      <div className={styles.roamVisual}>
        <RoamMap />
        <div className={styles.alert}>
          <span className={styles.alertApp}>
            <FiMark />
          </span>
          <span className={styles.alertText}>
            <span className={styles.alertHead}>
              {t('cat_first_roam_alert_title')}
              <span className={styles.alertTime}>{t('cat_first_roam_alert_time')}</span>
            </span>
            <span>{t('cat_first_roam_alert_body')}</span>
          </span>
        </div>
        <div className={styles.pin}>
          <img className={styles.pinPhoto} src="/product_images/hardware-first/lifestyle-cat.webp" alt="" />
          <span className={styles.pinLabel}>{t('cat_first_roam_status')}</span>
        </div>
        <div className={styles.legend}>
          {t('cat_first_roam_legend')}
          <span className={styles.legendScale}>
            {t('cat_first_roam_less')}
            <span className={styles.legendBar} />
            {t('cat_first_roam_more')}
          </span>
        </div>
      </div>
    </section>
  );
}

const VS_ROWS = ['how', 'where', 'alerts', 'lost', 'day', 'phones'].map((row) => ({
  labelKey: `cat_first_vs_${row}_label`,
  fiKey: `cat_first_vs_${row}_fi`,
  airtagKey: `cat_first_vs_${row}_airtag`,
}));

/** A plain white disc with a polished rim: an AirTag, drawn, without Apple's mark. */
function AirTagArt({ gradientPrefix }: { gradientPrefix: string }) {
  return (
    <svg width="56" height="56" viewBox="0 0 64 64" aria-hidden="true">
      <defs>
        <radialGradient id={`${gradientPrefix}-face`} cx="38%" cy="32%" r="75%">
          <stop offset="0" stopColor="#FFFFFF" />
          <stop offset=".7" stopColor="#F3F3F1" />
          <stop offset="1" stopColor="#DADAD6" />
        </radialGradient>
        <linearGradient id={`${gradientPrefix}-rim`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#F7F7F7" />
          <stop offset=".5" stopColor="#B9BBBE" />
          <stop offset="1" stopColor="#E8E9EA" />
        </linearGradient>
      </defs>
      <ellipse cx="32" cy="58" rx="18" ry="3" fill="#000" opacity=".08" />
      <circle cx="32" cy="31" r="26" fill={`url(#${gradientPrefix}-rim)`} />
      <circle cx="32" cy="30.5" r="24" fill={`url(#${gradientPrefix}-face)`} />
      <circle cx="32" cy="30.5" r="23.5" fill="none" stroke="#FFFFFF" strokeOpacity=".9" strokeWidth="1" />
      <ellipse cx="25" cy="21" rx="9" ry="5" fill="#FFFFFF" opacity=".7" transform="rotate(-30 25 21)" />
    </svg>
  );
}

function CheckMark() {
  return (
    <svg className={styles.vsMark} viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="10" fill="#07a157" />
      <path d="M7.5 12.5l3 3 6-6.5" fill="none" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" />
    </svg>
  );
}

function CrossMark() {
  return (
    <svg className={styles.vsMark} viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="10" fill="#e3e2de" />
      <path d="M8.5 8.5l7 7M15.5 8.5l-7 7" fill="none" stroke="#968d88" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

/**
 * Fi vs AirTag. For most cat owners the alternative to Fi is a $29 AirTag, so the page answers why
 * Fi costs more: its own gps and cellular, against bluetooth that only updates near an iPhone.
 * Wide screens get a three-column table with a raised Fi column; phones stack each row under its label.
 */
export function CatFirstAirTagCompare() {
  const { t } = useTranslation();
  const fiName = t('cat_first_vs_fi');
  const airtagName = t('cat_first_vs_airtag');
  return (
    <section className={styles.vs}>
      <div className={styles.vsHeader}>
        <div className={styles.eyebrow}>{t('cat_first_vs_eyebrow')}</div>
        <h2 className={styles.vsTitle}>{t('cat_first_vs_title')}</h2>
        <p className={styles.vsSub}>{t('cat_first_vs_sub')}</p>
      </div>

      <div className={styles.vsWide} role="table" aria-label={t('cat_first_vs_title')}>
        <div className={styles.vsLabels} role="rowgroup">
          {VS_ROWS.map((row) => (
            <span key={row.labelKey} className={styles.vsLabel} role="rowheader">
              {t(row.labelKey)}
            </span>
          ))}
        </div>
        <div className={styles.vsFi} role="rowgroup">
          <div className={styles.vsFiHead}>
            <span className={styles.vsDevice}>
              <img src={MINI_DEVICE_IMAGE} alt="" />
            </span>
            <span className={styles.vsName}>{fiName}</span>
          </div>
          {VS_ROWS.map((row) => (
            <span key={row.fiKey} className={styles.vsCell} role="cell">
              <CheckMark />
              {t(row.fiKey)}
            </span>
          ))}
        </div>
        <div className={styles.vsAirtag} role="rowgroup">
          <div className={styles.vsAirtagHead}>
            <AirTagArt gradientPrefix="cat-first-airtag-wide" />
            <span className={styles.vsNameMuted}>{airtagName}</span>
          </div>
          {VS_ROWS.map((row) => (
            <span key={row.airtagKey} className={styles.vsCellMuted} role="cell">
              <CrossMark />
              {t(row.airtagKey)}
            </span>
          ))}
        </div>
      </div>

      <div className={styles.vsNarrow}>
        <div className={styles.vsNarrowHead}>
          <span className={styles.vsNarrowDevice}>
            <span className={styles.vsDevice}>
              <img src={MINI_DEVICE_IMAGE} alt="" />
            </span>
            <span className={styles.vsName}>{fiName}</span>
          </span>
          <span className={styles.vsNarrowDevice}>
            <AirTagArt gradientPrefix="cat-first-airtag-narrow" />
            <span className={styles.vsNameMuted}>{airtagName}</span>
          </span>
        </div>
        <dl className={styles.vsNarrowRows}>
          {VS_ROWS.map((row) => (
            <div key={row.labelKey} className={styles.vsNarrowRow}>
              <dt className={styles.vsNarrowLabel}>{t(row.labelKey)}</dt>
              <dd className={styles.vsNarrowCells}>
                <span className={styles.vsNarrowFi}>
                  <CheckMark />
                  <span className={styles.srOnly}>{fiName}</span>
                  {t(row.fiKey)}
                </span>
                <span className={styles.vsNarrowAirtag}>
                  <CrossMark />
                  <span className={styles.srOnly}>{airtagName}</span>
                  {t(row.airtagKey)}
                </span>
              </dd>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
}

const TIMELINE = [
  { textKey: 'cat_first_day_event_outside_long', timeKey: 'cat_first_day_time_afternoon', tone: 'outside' },
  { textKey: 'cat_first_day_event_left_home', timeKey: 'cat_first_day_time_afternoon', tone: 'zone' },
  { textKey: 'cat_first_day_event_rest', timeKey: 'cat_first_day_time_morning', tone: 'rest' },
  { textKey: 'cat_first_day_event_outside_short', timeKey: 'cat_first_day_time_early', tone: 'outside' },
] as const;

/** The cat's day as the app shows it: active time and rest in place of steps, and the timeline. */
export function CatFirstDayInApp() {
  const { t } = useTranslation();
  return (
    <section className={styles.day}>
      <div className={styles.dayHeader}>
        <h2 className={sectionStyles.sectionHeading}>{t('cat_first_day_title')}</h2>
        <p className={styles.daySub}>{t('cat_first_day_sub')}</p>
      </div>
      <div className={styles.dayGrid}>
        <div className={styles.dayPhone}>
          <picture>
            <source srcSet={`${CAT_APP_SCREEN}.avif`} type="image/avif" />
            <source srcSet={`${CAT_APP_SCREEN}.webp`} type="image/webp" />
            <img src={`${CAT_APP_SCREEN}.png`} alt={t('cat_first_day_screenshot_alt')} />
          </picture>
        </div>
        <div className={styles.dayCards}>
          <div className={styles.dayStats}>
            <div className={styles.dayCard}>
              <span className={styles.dayOverline}>{t('cat_first_day_active_label')}</span>
              <span className={styles.dayActive}>{t('cat_first_day_active_value')}</span>
              <span className={styles.dayBarActive} />
            </div>
            <div className={styles.dayCard}>
              <span className={styles.dayOverline}>{t('cat_first_day_rest_label')}</span>
              <span className={styles.dayRest}>{t('cat_first_day_rest_value')}</span>
              <span className={styles.dayBarRest} />
            </div>
          </div>
          <div className={styles.dayCard}>
            <span className={styles.dayCardTitle}>{t('cat_first_day_last_outside_title')}</span>
            <span className={styles.dayCardSub}>{t('cat_first_day_last_outside_sub')}</span>
          </div>
          <div className={styles.dayCard}>
            <span className={styles.dayCardTitle}>{t('cat_first_day_timeline_title')}</span>
            <ul className={styles.timeline}>
              {TIMELINE.map((event) => (
                <li key={event.textKey} className={styles.timelineRow}>
                  <span className={styles[`dot_${event.tone}`]} />
                  <span className={styles.timelineText}>{t(event.textKey)}</span>
                  <span className={styles.timelineTime}>{t(event.timeKey)}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

const SPEC_ROWS: { Icon: SvgIcon; titleKey: string; subKey: string }[] = [
  { Icon: IconFeather, titleKey: 'cat_first_tile_weight', subKey: 'cat_first_spec_weight_sub' },
  { Icon: IconDrip, titleKey: 'fully_waterproof', subKey: 'ip68_tested_in_salt_water_no_dash' },
  { Icon: IconBatteryCharging, titleKey: 'up_to_2_months_of_battery_life', subKey: 'on_a_single_usb_c_charge' },
  { Icon: IconMagic, titleKey: 'featuring_fi_intelligence', subKey: 'cat_first_spec_intelligence_sub' },
  { Icon: IconCellular, titleKey: 'hardware_first_spec_verizon', subKey: 'verizon_lte_m_cellular_network' },
];

/** The cat specs, all five shown: the list is short enough that a show-all toggle only hides it. */
export function CatFirstSpecs() {
  const { t } = useTranslation();
  return (
    <section>
      <h2 className={sectionStyles.sectionHeading}>{t('cat_first_specs_title')}</h2>
      <div className={`${sectionStyles.specList} ${sectionStyles.specListSolo}`}>
        {SPEC_ROWS.map((row) => (
          <div key={row.titleKey} className={sectionStyles.specRow}>
            <span className={sectionStyles.specIcon}>
              <row.Icon />
            </span>
            <span className={sectionStyles.specText}>
              <span className={sectionStyles.specTitle}>{t(row.titleKey)}</span>
              <span className={sectionStyles.specSub}>{t(row.subKey)}</span>
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}

const CAT_FEATURE_KEYS = [
  'cat_first_feature_gps',
  'cat_first_feature_safe_zones',
  'cat_first_feature_lost_mode',
  'cat_first_feature_outside_time',
  'cat_first_feature_active_rest',
  'cat_first_feature_intelligence',
  'cat_first_feature_timeline',
  'cat_first_feature_vet_records',
  'cat_first_feature_family',
];

export function CatFirstAllFeatures() {
  const { t } = useTranslation();
  return (
    <section className={sectionStyles.featuresCard}>
      <h2 className={sectionStyles.featuresHeading}>{t('cat_first_features_title')}</h2>
      <div className={sectionStyles.featureList}>
        {CAT_FEATURE_KEYS.map((key) => (
          <div key={key} className={sectionStyles.featureRow}>
            <IconCheckCircle className={sectionStyles.featureCheck} />
            <span className={sectionStyles.featureLabel}>{t(key)}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

// both quotes are real App Store reviews from cat owners, credited to the review rather than a name
const CAT_REVIEWS = [
  { quoteKey: 'cat_first_review_miso_quote', creditKey: 'cat_first_review_app_store_credit' },
  { quoteKey: 'rescue_checkout_cat_proof_quote', creditKey: 'rescue_checkout_cat_proof_attribution' },
];

export function CatFirstReviews() {
  const { t } = useTranslation();
  return (
    <section>
      <h2 className={sectionStyles.sectionHeading}>{t('cat_first_reviews_title')}</h2>
      <div className={styles.reviews}>
        {CAT_REVIEWS.map((review) => (
          <figure key={review.quoteKey} className={styles.review}>
            <div className={styles.reviewStars} aria-label={t('cat_first_review_stars_label')} role="img">
              {Array.from({ length: 5 }).map((_, index) => (
                <IconStar key={index} className={sectionStyles.reviewStar} aria-hidden="true" />
              ))}
            </div>
            <blockquote className={styles.reviewQuote}>
              {t('hardware_first_quote_wrapped', { quote: t(review.quoteKey) })}
            </blockquote>
            <figcaption className={styles.reviewCredit}>{t(review.creditKey)}</figcaption>
          </figure>
        ))}
      </div>
    </section>
  );
}

/** The cats FAQ: what Fi tracks for a cat, indoor cats and collar fit first, then the membership. */
export function CatFirstFAQ({ showVerizonQuestion }: { showVerizonQuestion: boolean }) {
  const { t } = useTranslation();
  const questions: { title: string; body: React.ReactNode }[] = [
    { title: t('cat_first_faq_track_q'), body: t('cat_first_faq_track_a') },
    { title: t('cat_first_faq_indoor_q'), body: t('cat_first_faq_indoor_a') },
    {
      title: t('cat_first_faq_fit_q'),
      body: (
        <>
          {t('cat_first_faq_fit_a')}
          <ul>
            <li>{t('strap_minimum_none_maximum_1_25')}</li>
            <li>{t('soft_case_minimum_0_375_maximum_1_25')}</li>
            <li>{t('3_8_soft_case_minimum_0_375_maximum_0_375')}</li>
          </ul>
        </>
      ),
    },
    ...(showVerizonQuestion
      ? [
          {
            title: t('do_i_need_to_have_a_verizon_account_to_use_a_fi_mini'),
            body: t('verizon_account_not_required_answer'),
          },
        ]
      : []),
    {
      title: t('why_is_fi_a_membership'),
      body: (
        <>
          {t('why_is_fi_a_membership_answer_part1')}
          <br />
          <br />
          {t('why_is_fi_a_membership_answer_part2')}
        </>
      ),
    },
    {
      title: t('can_i_get_a_new_device_if_im_already_a_fi_member'),
      body: t('can_i_get_a_new_device_if_im_already_a_fi_member_answer'),
    },
  ];
  return (
    <div className={styles.faq}>
      <h2 className={sectionStyles.sectionHeading}>{t('faq')}</h2>
      <div>
        {questions.map((question, index) => (
          <Question key={question.title} title={question.title} page="Mini for cats" index={index}>
            {question.body}
          </Question>
        ))}
      </div>
    </div>
  );
}

/** The cat-first page's below-fold, in place of the shared one on the cats page. */
export function CatFirstBelowFold({ showVerizonQuestion }: { showVerizonQuestion: boolean }) {
  return (
    <div className={`${pageStyles.belowFold} ${pageStyles.belowFoldSolo}`}>
      <CatFirstRoamBand />
      <CatFirstAirTagCompare />
      <CatFirstDayInApp />
      <WhatsInTheBox deviceKey="mini" />
      <CatFirstSpecs />
      <CatFirstAllFeatures />
      <CatFirstReviews />
      {/* the shared faq rows key their compact treatment off this global class */}
      <div className="hardware-first-faq">
        <CatFirstFAQ showVerizonQuestion={showVerizonQuestion} />
      </div>
    </div>
  );
}
